import React, { useMemo, useState } from 'react';
import {
  Button, Grid, Heading, Link,
  List,
  Spinner, Modal, TextareaField,
} from '@printdeal/ui-library';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { FeatureToggle } from '@printdeal/feature-management/react';
import ProposalStatusBadge from './ProposalStatusBadge';
import { messages } from '../../translations/proposals';
import {
  Iteration,
  Status,
} from '../../../../services/customer/CustomerProposalService';
import useTranslationExists from '../../../../hooks/useTranslationExists';
import LineItem from '../../../../types/cart/LineItem';
import ProposalArtworkThumbnail from '../ProposalArtworkThumbnail';
import { ProductGallery } from '../../../product/detail/ProductGallery';
import { IProductGalleryImage } from '../../../../types/product/productDetailPage';
import { getGalleryImages } from '../../../product/detail/helpers/ProductGalleryHelper';

interface Props {
  iteration: Iteration;
  handleProposalReview: (status: Status, comments?: string) => void;
  isWaiting: boolean;
  promisedArrivalDate: string | undefined;
  cutoffTime: string | undefined;
  lineItem?: LineItem;
  version: number;
}

const ProposalIteration = ({
  iteration, isWaiting, handleProposalReview,
  cutoffTime, promisedArrivalDate, lineItem, version,
}: Props) => {
  const [updatingAction, setUpdatingAction] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [adjustmentText, setAdjustmentText] = useState('');
  const [openDeliverNewFileModal, setOpenDeliverNewFileModal] = useState(false);
  const intl = useIntl();

  const documentReference = !Array.isArray(iteration?.checkItResponse)
    ? iteration?.checkItResponse?.documentReferenceUrl : undefined;
  const customFields = lineItem?.custom?.fields;

  const {
    modifiedFile, status, createdAt, auxCodes,
  } = iteration;

  const translationExists = useTranslationExists();

  const handleAction = (action: Status, text?: string) => {
    setUpdatingAction(action);
    handleProposalReview(action, text);
  };

  const handleTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAdjustmentText(event.target.value);
  };

  const onSendAdjustment = () => {
    handleAction('closed', adjustmentText);
    setOpenModal(false);
  };

  const filteredAuxcodes = useMemo(() =>
    auxCodes.reduce<React.ReactNode[]>((acc, code) => {
      const translationId = `fileReview.code.${code}.description`;
      if (translationExists(translationId)) {
        acc.push(
          <List.Item key={code}>
            <FormattedMessage
              id={translationId}
            />
          </List.Item>,
        );
      }
      return acc;
    }, []),
  [auxCodes, translationExists]);

  const adjustmentModalElement = () => (
    <Modal
      title={(
        <p className="pd-text-md">
          <FormattedMessage
            id={messages.adjustmentTitle.id}
            defaultMessage={messages.adjustmentTitle.defaultMessage}
          />
        </p>
    )}
      isOpen={openModal}
      size="lg"
      onClose={() => setOpenModal(false)}
      dismissLabel="Dismiss"
    >
      <Modal.Content>
        <div className="pd-mb-4 pd-text-sm pd-font-semibold">
          <FormattedMessage
            id={messages.adjustmentText.id}
            defaultMessage={messages.adjustmentText.defaultMessage}
          />
          <span className="pd-text-error-500"> *</span>
        </div>
        <TextareaField
          id="adjustmentText"
          name=""
          value={adjustmentText}
          onChange={handleTextareaChange}
          rows={4}
        />
      </Modal.Content>
      <Modal.Footer className="pd-flex pd-justify-end pd-space-x-2">
        <Button
          variant="secondary"
          onClick={() => setOpenModal(false)}
        >
          <FormattedMessage
            id={messages.cancelAdjustmentButton.id}
            defaultMessage={
              messages.cancelAdjustmentButton.defaultMessage
            }
          />
        </Button>
        <Button variant="primary" onClick={onSendAdjustment} disabled={!adjustmentText.trim()}>
          <FormattedMessage
            id={messages.sendAdjustmentButton.id}
            defaultMessage={
              messages.sendAdjustmentButton.defaultMessage
            }
          />
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const newFileDeliveryElement = () => (
    <Modal
      title={(
        <p className="pd-text-lg">
          <FormattedMessage
            id={messages.deliverNewFileModalTitle.id}
            defaultMessage={messages.deliverNewFileModalTitle.defaultMessage}
          />
        </p>
    )}
      isOpen={openDeliverNewFileModal}
      size="lg"
      onClose={() => setOpenDeliverNewFileModal(false)}
      dismissLabel="Dismiss"
    >
      <Modal.Content>
        <div className="pd-my-2 pd-text-md pd-font-semibold">
          <FormattedMessage
            id={messages.deliverNewFileModalText.id}
            defaultMessage={messages.deliverNewFileModalText.defaultMessage}
          />
        </div>
      </Modal.Content>
      <Modal.Footer className="pd-flex pd-justify-end pd-space-x-2">
        <Button
          variant="secondary"
          onClick={() => setOpenDeliverNewFileModal(false)}
          disabled={isWaiting}
        >
          <FormattedMessage
            id={messages.deliverNewFileCancelButton.id}
            defaultMessage={
              messages.deliverNewFileCancelButton.defaultMessage
            }
          />
        </Button>
        <Button
          variant="primary"
          onClick={() => handleAction('rejected')}
          disabled={isWaiting}
          iconBefore={(isWaiting && updatingAction === 'rejected')
            ? (
              <Spinner
                variant="primary"
              />
            )
            : undefined}
        >
          <FormattedMessage
            id={messages.deliverNewFileModalConfirmButton.id}
            defaultMessage={
              messages.deliverNewFileModalConfirmButton.defaultMessage
            }
          />
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const expectedDeliveryDate = promisedArrivalDate && Date.parse(promisedArrivalDate) && (
    <FormattedDate
      month="long"
      day="numeric"
      weekday="long"
      value={promisedArrivalDate}
    />
  );

  const modifiedArtworkUrl = modifiedFile?.pdfUrl || modifiedFile?.modifiedUrl || (modifiedFile?.previewUrls?.[0] ?? '');

  const galleryControls = useMemo(() => ({ isNavExpandable: false }), []);

  const defaultImages = useMemo(() => {
    if (!modifiedFile?.previewUrls || modifiedFile?.previewUrls?.length === 0) return [];
    const images = modifiedFile.previewUrls.map((url) => ({
      url,
      title: url,
      width: 100,
      height: 100,
    }));
    return getGalleryImages(images as IProductGalleryImage[]);
  }, [modifiedFile?.previewUrls]);

  return (
    <Grid className="pd-gap-6">
      <div>
        <Heading as="h3" className="pd-mb-2">
          <FormattedMessage
            id={messages.proposalDetailTitle.id}
            defaultMessage={messages.proposalDetailTitle.defaultMessage}
          />{' '}
          <span>
            <FormattedMessage
              id={messages.iterationVersionTitle.id}
              defaultMessage={messages.iterationVersionTitle.defaultMessage}
              values={{ version }}
            />
          </span>
        </Heading>
        <ProposalStatusBadge status={status} />
        <span className="pd-px-3 pd-text-xs pd-text-dim-500">
          <FormattedMessage
            id={messages.onPublishDateText.id}
            defaultMessage={messages.onPublishDateText.defaultMessage}
          />{' '}
          <time dateTime={createdAt}>
            <FormattedDate
              value={createdAt}
              year="numeric"
              month="long"
              day="2-digit"
            />
          </time>
        </span>
      </div>
      <Grid className="pd-grid-cols-1 pd-items-start pd-gap-4 md:pd-grid-cols-4">
        <Grid.Item className="md:pd-col-span-1">
          {defaultImages.length
            ? (
              <ProductGallery
                imageComponents={defaultImages}
                thumbnailComponents={defaultImages}
                enlargedImageComponents={defaultImages}
                pageName="help_center_page"
                modalTitle={intl.formatMessage({
                  id: messages.modifiedArtworkPreview.id,
                  defaultMessage: messages.modifiedArtworkPreview.defaultMessage,
                },
                { lineItemNumber: customFields?.lineItemNumber || '' })}
                controls={galleryControls}
                maxThumbnails={1}
              />
            )
            : (
              <ProposalArtworkThumbnail
                lineItem={lineItem}
                documentReference={documentReference}
                userSelection={customFields?.userSelection}
                isDeliveredFileValid
                size="2xl"
                modifiedArtworkUrl={modifiedArtworkUrl}
              />
            )}
        </Grid.Item>
        <Grid.Item className="md:pd-col-span-2">
          <Heading as="h4">
            <FormattedMessage
              id={messages.proposalNotes.id}
              defaultMessage={messages.proposalNotes.defaultMessage}
            />
          </Heading>
          <List variant="decimal" className="pd-mb-2">
            {filteredAuxcodes}
          </List>
          <div className="pd-flex pd-gap-4">
            {
              (modifiedFile?.pdfUrl || modifiedFile?.modifiedUrl)
                ? (
                  <Link
                    href={modifiedFile.pdfUrl || modifiedFile.modifiedUrl}
                    rel="noopener noreferrer"
                    target="_blank"
                    variant="secondary"
                    data-cy="downloadFile"
                    iconBefore="download"
                  >
                    <FormattedMessage
                      id={messages.downloadArtwork.id}
                      defaultMessage="Modified File"
                    />
                  </Link>
                ) : null
            }
            {
              modifiedFile?.previewUrls?.length
                ? (
                  <Link
                    href={modifiedFile.previewUrls[0]}
                    rel="noopener noreferrer"
                    target="_blank"
                    variant="secondary"
                    data-cy="downloadFile"
                    iconBefore="presentation-category"
                  >
                    <FormattedMessage
                      id={messages.scenePreview.id}
                      defaultMessage="Scene Preview"
                    />
                  </Link>
                ) : null
            }
          </div>
        </Grid.Item>
        {status === 'AwaitingConfirmation' && (
          <Grid.Item className="pd-col-span-1 md:pd-col-span-1">
            <Button
              variant="primary"
              size="md"
              className="pd-mb-2 pd-w-full"
              disabled={isWaiting}
              onClick={() => handleAction('approved')}
              iconBefore={(isWaiting && updatingAction === 'approved')
                ? (
                  <Spinner
                    variant="primary"
                  />
                )
                : undefined}
            >
              <FormattedMessage
                id={messages.acceptButton.id}
                defaultMessage={messages.acceptButton.defaultMessage}
              />
            </Button>
            <div className="pd-mb-4 pd-px-4 pd-text-xs pd-text-dim-500">
              {(cutoffTime && promisedArrivalDate) ? (
                <FormattedMessage
                  id={messages.cutoffTimeAndPromisedArrivalDateNote.id}
                  defaultMessage={messages.cutoffTimeAndPromisedArrivalDateNote.defaultMessage}
                  values={{
                    cutoffTime: `${cutoffTime?.split(':')[0]}:${cutoffTime?.split(':')[1]}`,
                    promisedArrivalDate: expectedDeliveryDate,
                  }}
                />
              ) : (
                <div className="pd-flex pd-flex-row pd-items-center pd-justify-center">
                  <Spinner size="sm" />
                </div>
              )}
            </div>
            <FeatureToggle id="enable_proposal_adjustment">
              <FeatureToggle.Enabled>
                <Button
                  className="pd-mb-4 pd-w-full"
                  variant="secondary"
                  disabled={isWaiting}
                  onClick={() => setOpenModal(true)}
                  iconBefore={(isWaiting && updatingAction === 'closed')
                    ? (
                      <Spinner
                        variant="primary"
                      />
                    )
                    : undefined}
                >
                  <FormattedMessage
                    id={messages.adjustmentButton.id}
                    defaultMessage={messages.adjustmentButton.defaultMessage}
                  />
                </Button>
                {adjustmentModalElement()}
                {newFileDeliveryElement()}
              </FeatureToggle.Enabled>
            </FeatureToggle>
            <Button
              className="pd-mb-4 pd-w-full"
              disabled={isWaiting}
              variant="tertiary"
              onClick={() => setOpenDeliverNewFileModal(true)}
              iconBefore={(isWaiting && updatingAction === 'rejected')
                ? (
                  <Spinner
                    variant="primary"
                  />
                )
                : undefined}
            >
              <FormattedMessage
                id={messages.deliverNewFileButton.id}
                defaultMessage={messages.deliverNewFileButton.defaultMessage}
              />
            </Button>
          </Grid.Item>
        )}
      </Grid>
    </Grid>
  );
};

export default ProposalIteration;
